// DataSciencePage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'

const DataSciencePage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const dataScienceStats = [
        { number: '3.2x', label: 'higher ROI for businesses using predictive analytics' },
        { number: '85%', label: 'improvement in decision accuracy with machine learning models' },
        { number: '60%', label: 'reduction in customer churn through predictive modeling' },
        { number: '4.5x', label: 'faster insights compared to traditional analytics methods' }
    ];

    const dataScienceApproaches = [
        {
            icon: 'fas fa-road',
            title: 'Data Strategy & Framework Design',
            desc: 'Comprehensive data science roadmap architecture, data prioritization aligned with business objectives, and structured implementation plans for measurable impact.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Predictive Analytics & Forecasting',
            desc: 'Advanced algorithms and statistical models for trend forecasting, demand prediction, customer lifetime value modeling, and probability scoring for key outcomes.'
        },
        {
            icon: 'fas fa-brain',
            title: 'Machine Learning & Behavioral Insights',
            desc: 'Development and training of machine learning models for customer behavior analysis, including classification, clustering, recommendation engines, and segmentation.'
        },
        {
            icon: 'fas fa-bullseye',
            title: 'Attribution Modeling & Marketing Optimization',
            desc: 'Custom multi-touch attribution frameworks, marketing spend efficiency analysis, and predictive modeling for future campaign impact and ROI optimization.'
        },
        {
            icon: 'fas fa-users',
            title: 'Customer Analytics & Experience Optimization',
            desc: 'Deep customer behavior analysis, CRM integration, behavioral profiling, churn prediction, retention modeling, and journey analytics for personalized experiences.'
        },
        {
            icon: 'fas fa-tachometer-alt',
            title: 'Reporting, Dashboards & Operational Insights',
            desc: 'Custom data visualization dashboards, real-time performance tracking, executive reporting, and actionable insights delivery for strategic decision-making.'
        }
    ];

    const benefits = [
        ['Predictive Intelligence & Forecasting', 'Make proactive decisions based on future trends rather than reactive responses'],
        ['Enhanced Customer Understanding', 'Deep visibility into behavior patterns, preferences, and lifetime value optimization'],
        ['Optimized Resource Allocation', 'Data-driven budget and resource planning for maximum ROI and efficiency'],
        ['Scalable Insight Pipelines', 'Future-ready data infrastructure that grows with your business needs'],
        ['Competitive Strategic Advantage', 'Move faster and smarter than competitors with data-driven decision frameworks']
    ];

    const targetBusinesses = [
        'Organizations needing to move beyond intuition to evidence-based decision making',
        'Companies seeking to improve customer targeting, retention, and lifetime value',
        'Businesses requiring accurate forecasting and predictive business intelligence',
        'Marketing teams needing advanced attribution and campaign optimization insights',
        'Enterprises developing scalable intelligence systems for digital transformation',
        'Growth-focused businesses leveraging data as a strategic competitive asset'
    ];

    const dataScienceQuestions = [
        'What hidden patterns exist in your customer behavior data?',
        'How can you predict and prevent customer churn before it happens?',
        'What future trends should inform your strategic planning?',
        'How can machine learning optimize your marketing spend efficiency?',
        'What data infrastructure supports scalable growth and insights?'
    ];

    return (
        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Data Science</span> Services
                    </h1>
                    <p>Turn your data into predictive intelligence and strategic advantage</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In a world where every interaction creates data, the real value lies in transforming that raw information into actionable insights that directly fuel strategic decisions.
                                </p>
                                <p className="sv-description">
                                    Reachtix's <strong>Data Science services</strong> help businesses convert complexity into clarity — using predictive analytics, machine learning, and advanced modelling to understand behavior, forecast trends and unlock new opportunities for growth.
                                </p>
                                <p className="sv-description">
                                    Every brand captures data — but data alone isn't enough. It becomes valuable only when it tells a story and informs action. Our data science team turns your data into a competitive advantage, helping you increase conversions, optimise spend, and make decisions grounded in evidence rather than guesswork.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Data Science Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Data Science Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Data Science Matters</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {dataScienceStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Data Science Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Data Science?</h2>
                                <p className="sv-section-description">
                                    Data science empowers organisations to extract meaningful patterns and trends from large datasets, predict future customer behaviour and outcomes, and identify hidden insights that drive smarter business decisions.
                                </p>
                                <p className="sv-section-description">
                                    With the increasing volume and complexity of data, traditional analysis is no longer enough — modern businesses need data science to stay competitive, adaptive and forward-looking.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Data Science Questions We Address:</h4>
                                    {dataScienceQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Data Science Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Data Science Drives Business Success</h2>
                                <p className="sv-section-description">
                                    Data science transforms raw data into strategic intelligence, enabling organizations to move from reactive decision-making to predictive, proactive business strategies.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-brain"></i>
                                        <h5>Predictive Intelligence</h5>
                                        <p>Anticipate trends and outcomes with advanced forecasting models</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-robot"></i>
                                        <h5>Machine Learning Automation</h5>
                                        <p>Automate insight generation and decision optimization processes</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Customer Behavior Analysis</h5>
                                        <p>Deep understanding of customer journeys and preferences</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-pie"></i>
                                        <h5>Operational Optimization</h5>
                                        <p>Data-driven efficiency improvements across business processes</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Data Science Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Data Science Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer a full suite of services that blend statistical expertise, machine learning, and business strategy to deliver real, measurable impact for your organization.
                                </p>

                                <div className="sv-approaches-grid">
                                    {dataScienceApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Data Science with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Data Science?</h2>
                                <p className="sv-section-description">
                                    Data science delivers maximum value for organizations seeking to leverage data as a strategic asset for growth and optimization:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're a growing startup or an established enterprise, robust data science capabilities unlock insights that transform performance and drive sustainable growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Turn Data Into Business Growth?</h2>
                                <p className="sv-cta-description">
                                    Unlock meaningful insights and make smarter decisions with Reachtix's <strong>Data Science services</strong>. Transform your data from a passive resource into an active growth engine.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we don't treat data as a by-product — we treat it as a strategic asset. Our experts combine analytical rigor with commercial understanding to turn your data into insights that matter.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        className="sv-cta-primary-btn"
                                    >
                                        <i className="fas fa-brain mr-2"></i>
                                        Start Data Science Journey
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        className="sv-cta-secondary-btn"
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Data Science Assessment
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DataSciencePage;